import { Box } from "@chakra-ui/react"
import { History } from "history"
import { useEffect, useRef, useState } from "react"
import {
  Route,
  RouteComponentProps,
  Router,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom"
import { usePreviousDistinct } from "react-use"
import { resolveToFromLinkProps } from "../link/Link"
import {
  AugmentationToolbarChildren,
  AugmentationToolbarHookReturn,
  AugmentationToolbarOption,
  isOptionVisible
} from "./AugmentationToolbar"
import useQueryParamHistory, {
  useQueryParamState
} from "./useQueryParamHistory"

type AugmentationToolbarRoutingProps = {
  toolbar: AugmentationToolbarHookReturn
  children: (routes: React.ReactNode) => React.ReactNode
  queryParamKey: string
}

function getLocationPath(location: {
  pathname: string
  search: string
  hash: string
}) {
  return location.pathname + location.search + location.hash
}

type OnEnterRoutesProps = {
  toolbar: AugmentationToolbarHookReturn
  option: AugmentationToolbarOption
}

// When a route is entered, select the root toolbar option.
function OnEnterRoutes({ toolbar, option }: OnEnterRoutesProps) {
  const key = option.compositeKey || option.key

  useEffect(() => {
    toolbar.onSelect(key)
  }, [key])

  return null
}

type OnEnterRouteProps = {
  rootHistory: History
  queryParamKey: string
}

// When a route is entered, update the root history location with the new query param path.
function OnEnterRoute({ rootHistory, queryParamKey }: OnEnterRouteProps) {
  const location = useLocation()
  const panelHistory = useHistory()
  const nextQueryParamPath = getLocationPath(location)

  // Keyed on the destination rather than on mount. A <Switch> deliberately
  // reuses a route that renders the same component at another URL, so an
  // option's alternate paths and its parameterized entries all share one
  // mount — and mount is the one moment this would otherwise miss.
  useEffect(() => {
    const currentRootHistoryPath = getLocationPath(rootHistory.location)
    // Rather than using setQueryParamPath(location.pathname), we're using the root history
    // which (interestingly) allows for better native navigation (like browser back/forward buttons).
    const url = new URL(window.location.origin + currentRootHistoryPath)
    url.searchParams.set(queryParamKey, nextQueryParamPath)

    // Setting updated query param in current url, navigating to it.
    const nextRootHistoryPath = getLocationPath(url)

    if (nextRootHistoryPath === currentRootHistoryPath) return

    const to = {
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
      // Carried through so panel navigation doesn't drop what the app put on
      // the entry, like the Cover Page close target.
      state: rootHistory.location.state
    }

    // Mirror how the panel navigated. Drilling into a destination is a step the
    // user expects Back to undo one level at a time, so it earns a browser
    // entry; a replace (hash focus, redirects) is not a step and doesn't.
    if (panelHistory.action === "PUSH") {
      rootHistory.push(to)
    } else {
      rootHistory.replace(to)
    }
  }, [nextQueryParamPath])

  return null
}

function isThenable(value: unknown): value is Promise<unknown> {
  return !!value && typeof (value as Promise<unknown>).then === "function"
}

// When a route is entered, call it's middleware function.
function OnEnterRouteMiddleware({
  routeProps,
  option
}: {
  routeProps: RouteComponentProps<any>
  option: AugmentationToolbarOption
}) {
  const matchUrl = routeProps.match.url

  // Middleware loads by route param into a shared store, and the route stays
  // mounted while only that param changes, so runs can overlap. Queueing keeps
  // the last destination entered the last one written.
  const pendingRef = useRef<Promise<unknown> | null>(null)

  useEffect(() => {
    const run = () => option.middleware?.(routeProps)

    // Nothing in flight is the common case, and deferring it would delay the
    // panel's first paint of real data by a tick.
    const result = pendingRef.current
      ? // Both handlers, so a rejected run doesn't strand the queue.
        pendingRef.current.then(run, run)
      : run()

    if (!isThenable(result)) {
      pendingRef.current = null
      return
    }

    pendingRef.current = result
    // finally, not then: clears the slot while leaving a rejection to surface
    // the way an unqueued middleware call always has.
    result.finally(() => {
      if (pendingRef.current === result) pendingRef.current = null
    })
  }, [option.compositeKey, matchUrl])

  return null
}

// When no route matches, we call the on404 callback
// to let the app router handle the missing route.
function OnEnterMissingRoute({
  rootHistory,
  queryParamKey
}: {
  rootHistory: History
  queryParamKey: string
}) {
  const location = useLocation()
  const history = useHistory()
  const route = getLocationPath(location)

  useEffect(() => {
    const url = new URL(window.location.origin + route)
    const queryParamPath = url.searchParams.get(queryParamKey)
    if (location.pathname === rootHistory.location.pathname && queryParamPath) {
      history.replace(queryParamPath)
      // Also replace the root history location to the current pathname, which essentially clears the query param path.
      rootHistory.replace(route)
    } else {
      rootHistory.replace(route)
    }
  }, [route])

  return null
}

function StatefulAugmentationToolbarRouting({
  toolbar,
  queryParamKey,
  children
}: AugmentationToolbarRoutingProps) {
  // Use the app route history.
  const rootHistory = useHistory()
  const queryParamHistory = useQueryParamHistory(queryParamKey)

  // Flatten all routes into a single array.
  // This allows us to sort them by specificity and render them in order.
  // A flattened structure also means only 1 route is rendered at a time (
  // we don't need nested routes).
  function flattenRoutes(
    children: AugmentationToolbarChildren,
    basePath = ""
  ): Array<{ path: string; option: any }> {
    const routes: Array<{ path: string; option: any }> = []

    children.forEach((option) => {
      const paths = Array.isArray(option.path)
        ? option.path
        : [option.path || option.key]

      paths.forEach((pathLeaf) => {
        const path = basePath ? `${basePath}/${pathLeaf}` : `/${pathLeaf}`

        if (option.AugmentationComponent) {
          routes.push({ path, option })
        }

        // A route grouping can omit its own component while still contributing
        // its path and breadcrumb label to descendant routes.
        if (option.children) {
          routes.push(...flattenRoutes(option.children, path))
        }
      })
    })

    return routes
  }

  const allRoutes = flattenRoutes(toolbar.options)

  // Sort routes by specificity (most specific first)
  const sortedRoutes = allRoutes.sort((a, b) => {
    return b.path.split("/").length - a.path.split("/").length
  })

  // To match all routes, we use a wildcard route.
  const missingRoutePath = "/(.+)"

  return (
    <Router history={queryParamHistory}>
      {/* Render a "glob" type Route that matches all routes and selects the matching toolbar option. */}
      <Route
        path={sortedRoutes.map(({ path }) => path)}
        exact
        render={({ match }) => (
          <OnEnterRoutes
            toolbar={toolbar}
            option={
              sortedRoutes.find(({ path }) => path === match.path)?.option
            }
          />
        )}
      />
      {/* Handle route middleware */}
      {sortedRoutes.map(({ path, option }) => (
        <Route
          key={path}
          path={path}
          render={(props) => (
            <OnEnterRouteMiddleware routeProps={props} option={option} />
          )}
        />
      ))}
      <Switch>
        <Route
          path={[...sortedRoutes.map(({ path }) => path), missingRoutePath]}
          exact
          render={() =>
            children(
              // Routes swap without a transition. Animating the whole container
              // was tried and pulled back out: panel navigation blocks the main
              // thread long enough that the motion stuttered rather than
              // smoothed it, and an exit animation gated the incoming route's
              // mount behind it. See AP-15 and AP-16 in the Augmentation Panel
              // Refinement plan — the next attempt should animate the arriving
              // sections rather than the container, once the cost is down.
              <Box
                display="flex"
                minH="100%"
                w="100%"
                css={{ "& > *": { maxW: "100%" } }}
              >
                <Switch>
                  {sortedRoutes.map(({ path, option }) => (
                    <Route
                      // Option routes may have multiple paths, but since we're breaking out the paths into separate
                      // routes, we need to use the compositeKey to ensure the component isn't unmounted/remounted unnecessarily.
                      key={option.compositeKey}
                      path={path}
                      exact
                      render={(route) => (
                        <>
                          <OnEnterRoute
                            rootHistory={rootHistory}
                            queryParamKey={queryParamKey}
                          />
                          <option.AugmentationComponent
                            toolbar={toolbar}
                            route={route}
                          />
                        </>
                      )}
                    />
                  ))}
                  {/* If the query param history is greater than 1, we render the missing route which
                works since we don't want to handle missing route when we haven't hanedled any matching routes yet. */}
                  {queryParamHistory.length > 1 && (
                    <Route path={missingRoutePath} key="missing-route">
                      <OnEnterMissingRoute
                        rootHistory={rootHistory}
                        queryParamKey={queryParamKey}
                      />
                    </Route>
                  )}
                </Switch>
              </Box>
            )
          }
        />
      </Switch>
    </Router>
  )
}

// Wraps the StatefulAugmentationToolbarRouting component and handles the key state,
// which is used to force a re-mounting of the component when the key changes.
// We do this when exiting the augmentation router or when the toolbar stack empties.
// This is in order to reset the query param history.

// NOTE: there are 2 scenarios we need to handle:
// 1. queryParamPath is set but toolbar stack is empty => either:
//   - clear the queryParamPath if stack emptied (we exited the toolbar manually) or
//   - select the toolbar option (we entered the toolbar via route)
// 2. queryParamPath is null but toolbar stack is not empty => set the queryParamPath to the base (first) toolbar option
function AugmentationToolbarRouting({
  toolbar,
  queryParamKey,
  children
}: AugmentationToolbarRoutingProps) {
  const { queryParamPath, setQueryParamPath } =
    useQueryParamState(queryParamKey)
  const [key, setKey] = useState(0)

  const baseOption = toolbar.stack[0]
  const baseOptionIsSelected = toolbar.stack.length === 1
  const prevBaseOption = usePreviousDistinct(baseOption)

  // Scenario 1: Respond to the change of the toolbar stack by setting the query param path.
  useEffect(() => {
    if (baseOption && baseOptionIsSelected) {
      let to = resolveToFromLinkProps(baseOption, queryParamPath || "")

      // Default to the key if no path is provided.
      if (!baseOption.path || baseOption.path === baseOption.key) {
        to ??= `/${baseOption.key}`
      } else {
        // DevX: If the option is visible (meaning directly accessible/clickable to the user) we warn that the to/relativeTo props are not set.
        if (!to && isOptionVisible(baseOption as AugmentationToolbarOption)) {
          console.warn(
            `Toolbar option ${baseOption.key} has a path but no to/relativeTo prop was set. If you provide a path, you must specify a to/relativeTo prop on the option.`
          )
        }
      }

      // Re-asserting the path the URL already holds would push a duplicate, and
      // a push from a non-tip entry discards everything ahead of it — so a Back
      // onto the base option would take its own Forward with it.
      if (to && to !== queryParamPath) {
        setQueryParamPath(to)
      }
    } else if (!baseOption && prevBaseOption && queryParamPath) {
      // Closing is a step of its own, not an unwind of the ones before it.
      // Unwinding moved the panel's entries onto the forward stack, so Back
      // walked off the page instead of reopening where the user left off.
      setQueryParamPath()
    }
  }, [baseOption?.key, baseOptionIsSelected, prevBaseOption?.key])

  // Scenario 2: Respond to the "exiting" of the query param path by bumping the key
  // as well as clearing the toolbar stack.
  useEffect(() => {
    if (!queryParamPath) {
      toolbar.onSelect(null)
      setKey((key) => key + 1)
    }
  }, [queryParamPath])

  return (
    <StatefulAugmentationToolbarRouting
      key={key}
      toolbar={toolbar}
      queryParamKey={queryParamKey}
    >
      {children}
    </StatefulAugmentationToolbarRouting>
  )
}

export default AugmentationToolbarRouting
